import { isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  Directive,
  ElementRef,
  inject,
  OnDestroy,
  PLATFORM_ID,
} from '@angular/core';

/**
 * Adds a second, synchronised horizontal scrollbar above a `.table-container`.
 *
 * Wide tables are often tall, so a scrollbar that only sits underneath the
 * table cannot be reached until the reader has scrolled past every row. This
 * mirrors it above the table and keeps both in sync. The bar is hidden when
 * the table fits, and is decorative, so it is removed from the tab order and
 * from the accessibility tree.
 */
@Directive({
  selector: '.table-container',
})
export class TableScrollTop implements AfterViewInit, OnDestroy {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly platformId = inject(PLATFORM_ID);

  private bar?: HTMLElement;
  private spacer?: HTMLElement;
  private observer?: ResizeObserver;
  private syncing = false;

  ngAfterViewInit(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const container = this.host.nativeElement;
    const parent = container.parentElement;
    if (!parent) {
      return;
    }

    const bar = document.createElement('div');
    bar.className = 'table-scroll-top';
    bar.setAttribute('aria-hidden', 'true');

    const spacer = document.createElement('div');
    spacer.className = 'table-scroll-top__spacer';
    bar.appendChild(spacer);
    parent.insertBefore(bar, container);

    this.bar = bar;
    this.spacer = spacer;

    bar.addEventListener('scroll', this.onBarScroll, { passive: true });
    container.addEventListener('scroll', this.onContainerScroll, {
      passive: true,
    });

    this.sync();

    // Keep the spacer width and the bar's visibility correct as the table
    // reflows (viewport resize, font loading, orientation change).
    if (typeof ResizeObserver !== 'undefined') {
      this.observer = new ResizeObserver(() => this.sync());
      this.observer.observe(container);
      const table = container.querySelector('table');
      if (table) {
        this.observer.observe(table);
      }
    }
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
    this.bar?.removeEventListener('scroll', this.onBarScroll);
    this.host.nativeElement.removeEventListener(
      'scroll',
      this.onContainerScroll
    );
    this.bar?.remove();
  }

  private readonly onBarScroll = (): void => {
    if (this.syncing || !this.bar) {
      return;
    }
    this.syncing = true;
    this.host.nativeElement.scrollLeft = this.bar.scrollLeft;
    this.syncing = false;
  };

  private readonly onContainerScroll = (): void => {
    if (this.syncing || !this.bar) {
      return;
    }
    this.syncing = true;
    this.bar.scrollLeft = this.host.nativeElement.scrollLeft;
    this.syncing = false;
  };

  private sync(): void {
    const container = this.host.nativeElement;
    if (!this.bar || !this.spacer) {
      return;
    }

    const scrollWidth = container.scrollWidth;
    this.spacer.style.width = `${scrollWidth}px`;

    // Nothing to scroll: keep the bar out of the layout entirely.
    const overflows = scrollWidth > container.clientWidth + 1;
    this.bar.hidden = !overflows;

    if (overflows) {
      this.bar.scrollLeft = container.scrollLeft;
    }
  }
}
